import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABELS = {
  admin: 'Cancelled by you',
  patient: 'Cancelled by patient',
  stripe_dashboard: 'Refunded in Stripe',
  late_payment: 'Late payment, slot taken',
};

const fmt = (iso) => new Date(iso).toLocaleString('en-GB', { timeZone: 'Europe/Madrid', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export const RefundHistory = ({ headers, onUnauthorized, refreshKey }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [reload, setReload] = useState(0);

  const syncStripe = async () => {
    setSyncing(true); setSyncMsg('');
    try {
      const r = await fetch(`${API_URL}/api/admin/refunds/sync`, { method: 'POST', headers: headers() });
      if (r.status === 401) { onUnauthorized(); return; }
      const d = await r.json();
      setSyncMsg(r.ok ? d.message : (d.detail || 'Sync failed. Please try again.'));
      if (r.ok) setReload(x => x + 1);
    } catch { setSyncMsg('Connection problem. Please try again.'); }
    finally { setSyncing(false); }
  };

  useEffect(() => {
    setError('');
    fetch(`${API_URL}/api/admin/refunds`, { headers: headers() })
      .then(async r => {
        if (r.status === 401) { onUnauthorized(); return; }
        if (!r.ok) throw new Error();
        setData(await r.json());
      })
      .catch(() => setError('Could not load refund history. Please try again.'));
  }, [headers, onUnauthorized, refreshKey, reload]);

  if (error) return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="refunds-error">{error}</div>;
  if (!data) return <Loader2 className="animate-spin text-[#b8860b]" />;

  return (
    <div className="bg-white border border-[#e2e8f0]" data-testid="refund-history">
      <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-b border-[#e2e8f0]">
        <h2 className="font-semibold text-[#0f172a]">Refund history</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#475569]" data-testid="refunds-total">
            {data.refunds.length} refund{data.refunds.length === 1 ? '' : 's'} · Total €{data.total.toFixed(2)}
          </span>
          <button onClick={syncStripe} disabled={syncing} data-testid="refunds-sync-btn"
            className="px-3 py-1.5 text-xs border border-[#e2e8f0] hover:border-[#b8860b] transition-colors disabled:opacity-50">
            {syncing ? 'Checking Stripe...' : 'Sync with Stripe'}
          </button>
        </div>
      </div>
      {syncMsg && <p className="px-4 py-2 text-sm bg-[#fdf8ec] text-[#7a5a0b] border-b border-[#ecd9a8]" data-testid="refunds-sync-msg">{syncMsg}</p>}
      {data.refunds.length === 0 ? (
        <p className="px-4 py-6 text-sm text-[#64748b]" data-testid="refunds-empty">No refunds yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f8fafc] text-left text-xs uppercase tracking-wider text-[#64748b]">
              <tr><th className="px-4 py-2">Refunded</th><th className="px-4 py-2">Patient</th><th className="px-4 py-2">Appointment</th><th className="px-4 py-2">Reason</th><th className="px-4 py-2 text-right">Amount</th></tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8f0]">
              {data.refunds.map((r, i) => (
                <tr key={`${r.booking_id}-${i}`} data-testid={`refund-row-${r.refund_id || `${r.booking_id}-${i}`}`}>
                  <td className="px-4 py-2 whitespace-nowrap">{fmt(r.at)}</td>
                  <td className="px-4 py-2">{r.patient_name}<div className="text-xs text-[#94a3b8]">{r.booking_id}</div></td>
                  <td className="px-4 py-2 whitespace-nowrap">{r.service === 'home_visit' ? 'Home Visit' : 'Telemedicine'} · {r.date} {r.time}</td>
                  <td className="px-4 py-2">{SOURCE_LABELS[r.source] || r.source}</td>
                  <td className="px-4 py-2 text-right font-medium" data-testid={`refund-amount-${r.refund_id || `${r.booking_id}-${i}`}`}>
                    €{r.amount.toFixed(2)}
                    {r.amount < r.total_amount && <div className="text-xs text-[#94a3b8]">of €{r.total_amount.toFixed(2)}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
